/**
 * The theme's one Smile client. Reads the loyalty programme's live numbers and
 * writes them into whatever markup asked for them.
 *
 * Every rate in the theme is also rendered server-side from theme settings, so
 * pages paint complete and are indexable. This script's only job is to correct
 * those numbers against Smile so they can't quietly drift after someone edits
 * the programme in Smile admin.
 *
 * The rates come from Smile's storefront API, the same one Smile's own widget
 * uses: the channel key sits in the app embed's markup, it exchanges for a
 * publishable key, and that reads /earning_rules and /points_products. Both
 * keys are public by design — no private API key, and nothing here can write.
 *
 * Cached six hours in localStorage, so it costs two requests per browser per
 * session at most, and none on a normal repeat visit.
 *
 * Markup hooks:
 *   [data-smile-points]       cart row — recalculates points for data-total
 *   [data-smile-earn-rate]    points earned per £1
 *   [data-smile-redeem-rate]  points needed per £1 off
 *   [data-smile-back-pct]     the two above as a percentage back
 *   [data-smile-balance]      signed-in customer's balance
 *   [data-smile-balance-value] that balance as money
 *   [data-smile-balance-block] container, unhidden once a balance is known
 */
(function () {
  if (window.__smileLoyaltyLoaded) return;
  window.__smileLoyaltyLoaded = true;

  var CACHE_KEY = 'smile:program:v2';
  var CACHE_TTL = 6 * 60 * 60 * 1000;
  var API_HOST = 'https://api.smile.io/smile_js';
  var PLATFORM_HOST = 'https://platform.smile.io/smile_ui';

  // { earn: points per £1 spent, redeem: points per £1 off }
  var program = null;
  var pending = null;

  // ── Rendering ──────────────────────────────────────

  function each(selector, fn) {
    document.querySelectorAll(selector).forEach(fn);
  }

  function num(el, attr) {
    var raw = parseFloat(el.getAttribute(attr));
    return isNaN(raw) ? null : raw;
  }

  function apply() {
    var earn = program ? program.earn : null;
    var redeem = program ? program.redeem : null;

    each('[data-smile-points]', function (el) {
      var total = parseInt(el.dataset.total, 10) || 0;
      var rate = earn === null ? parseFloat(el.dataset.rate) : earn;
      if (!rate || rate <= 0 || total <= 0) {
        el.hidden = true;
        return;
      }
      var points = Math.floor((total / 100) * rate);
      var text = el.querySelector('[data-smile-points-text]') || el;
      text.textContent = el.dataset.template.replace('__POINTS__', points.toLocaleString());
      el.hidden = points <= 0;
    });

    if (earn !== null) {
      each('[data-smile-earn-rate]', function (el) { el.textContent = earn.toLocaleString(); });
    }
    if (redeem !== null) {
      each('[data-smile-redeem-rate]', function (el) { el.textContent = redeem.toLocaleString(); });
    }
    if (earn !== null && redeem !== null && redeem > 0) {
      var pct = Math.round((earn / redeem) * 1000) / 10;
      each('[data-smile-back-pct]', function (el) { el.textContent = pct.toLocaleString(); });
    }
  }

  // ── Programme rates ────────────────────────────────

  function readCache() {
    try {
      var entry = JSON.parse(localStorage.getItem(CACHE_KEY));
      if (!entry || Date.now() - entry.at > CACHE_TTL) return null;
      return { earn: entry.earn, redeem: entry.redeem };
    } catch (err) {
      return null;
    }
  }

  function writeCache(p) {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify({ earn: p.earn, redeem: p.redeem, at: Date.now() }));
    } catch (err) {
      /* private browsing, quota — the server-rendered rates still stand */
    }
  }

  function publishableKey() {
    if (window.__smile_ui_publishable_key__) {
      return Promise.resolve(window.__smile_ui_publishable_key__);
    }
    var embed = document.querySelector('.smile-shopify-init');
    var channelKey = embed && embed.dataset.channelKey;
    if (!channelKey) return Promise.reject(new Error('Smile app embed not found'));

    return fetch(PLATFORM_HOST + '/exchange_tokens?channel_key=' + encodeURIComponent(channelKey), {
      headers: { Accept: 'application/json' }
    })
      .then(function (res) { return res.json(); })
      .then(function (data) {
        if (!data.publishable_key) throw new Error('Smile returned no publishable key');
        return data.publishable_key;
      });
  }

  function get(key, path) {
    return fetch(API_HOST + path, {
      headers: {
        Accept: 'application/json',
        'Smile-Client': 'smile-js',
        'X-Smile-Publishable-Key': key
      }
    }).then(function (res) { return res.json(); });
  }

  /**
   * Points per £1 spent. Skips tier-gated rules — those apply to some
   * customers, not to whoever is reading the page, so the base rule is the
   * only one that is always true.
   */
  function earnRate(data) {
    var rules = (data && data.earning_rules) || [];
    for (var i = 0; i < rules.length; i++) {
      var rule = rules[i];
      if (!rule.reward || rule.reward.type !== 'points') continue;
      if (!rule.reward_value || rule.reward_value.type !== 'variable') continue;
      if (rule.restricted_to_vip_tier_ids && rule.restricted_to_vip_tier_ids.length) continue;

      var variable = rule.reward_value.variable || {};
      var per = variable.per_amount || 1;
      if (!variable.value || per <= 0) continue;
      return variable.value / per;
    }
    return null;
  }

  /** Points needed per £1 off, from the variable-value order discount. */
  function redeemRate(data) {
    var products = (data && data.points_products) || [];
    for (var i = 0; i < products.length; i++) {
      var p = products[i];
      if (p.exchange_type !== 'variable') continue;
      var step = p.variable_points_step;
      var value = p.variable_points_step_reward_value;
      if (!step || !value || value <= 0) continue;
      return step / value;
    }
    return null;
  }

  function fetchProgram() {
    return publishableKey().then(function (key) {
      return Promise.all([get(key, '/earning_rules'), get(key, '/points_products')]);
    }).then(function (results) {
      var earn = earnRate(results[0]);
      var redeem = redeemRate(results[1]);
      if (earn === null && redeem === null) return null;
      return { earn: earn, redeem: redeem };
    });
  }

  // ── Customer balance ───────────────────────────────

  /**
   * Smile's app embed already identifies the signed-in customer and parks the
   * response on the window, so the balance costs us no extra request and no
   * signing key. Fails closed: if the customer is a guest, or Smile has not
   * identified them, the block stays hidden rather than showing a zero.
   */
  function balance() {
    if (!document.querySelector('[data-smile-balance-block]')) return;
    if (!window.__smile_ui_customer_data__) return;

    Promise.resolve(window.__smile_ui_customer_data__).then(function (data) {
      var customer = (data && data.customer) || data || {};
      var points = customer.points_balance;
      if (typeof points !== 'number') return;

      each('[data-smile-balance]', function (el) { el.textContent = points.toLocaleString(); });

      each('[data-smile-balance-value]', function (el) {
        var redeem = program ? program.redeem : num(el, 'data-redeem-rate');
        if (!redeem || redeem <= 0) return;
        el.textContent = formatMoney(el, (points / redeem));
      });

      each('[data-smile-balance-block]', function (el) { el.hidden = false; });
    }).catch(function () {
      /* leave the block hidden */
    });
  }

  function formatMoney(el, amount) {
    var format = el.getAttribute('data-money-format') || '£{{amount}}';
    var fixed = amount.toFixed(2);
    return format
      .replace('{{amount}}', fixed)
      .replace('{{amount_no_decimals}}', String(Math.floor(amount)))
      .replace('{{amount_with_comma_separator}}', fixed.replace('.', ','))
      .replace('{{amount_no_decimals_with_comma_separator}}', String(Math.floor(amount)));
  }

  // ── Lifecycle ──────────────────────────────────────

  function refresh() {
    apply();

    if (program) {
      balance();
      return;
    }

    var cached = readCache();
    if (cached) {
      program = cached;
      apply();
      balance();
      return;
    }

    if (!pending) {
      pending = fetchProgram().catch(function () { return null; });
    }
    pending.then(function (p) {
      if (p) {
        program = p;
        writeCache(p);
        apply();
      }
      balance();
    });
  }

  // The drawer re-renders itself through the Section Rendering API, which
  // brings back the server-rendered numbers each time.
  document.addEventListener('cart:refreshed', refresh);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refresh);
  } else {
    refresh();
  }
})();
